/**
 * Двухпанельные диалоги: слева список юзеров, справа thread + composer.
 *
 * Polling:
 *   - список диалогов — каждые 10с (фоновый refresh бейджей)
 *   - активный thread — каждые 5с (видимый юзеру)
 *   - mark-read при выборе диалога (вход в thread)
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  List, Card, Avatar, Typography, Tag, Empty, Spin, Space, Badge, Button,
} from "antd";
import { UserOutlined, MessageOutlined, ReloadOutlined } from "@ant-design/icons";
import { API_URL } from "../../dataProvider";
import { ChatComposer } from "../../components/ChatComposer";
import { fmtDateTime } from "../../utils";

interface DialogItem {
  tg_id: number;
  tg_username: string | null;
  first_name: string | null;
  last_name: string | null;
  bot_blocked: boolean;
  flagged_abuse: boolean;
  last_seen_at: string | null;
  online: boolean;
  last_message_at: string | null;
  unread: number;
  last_direction: "incoming" | "outgoing";
  last_blocks: Array<{ type: string; text?: string; html?: string; caption?: string; file_name?: string }>;
}

interface ChatMessage {
  id: number;
  direction: "incoming" | "outgoing";
  sender: string | null;
  content_blocks: Array<{ type: string; [k: string]: unknown }>;
  tg_message_id: number | null;
  is_read: boolean;
  // TG-style read receipt для outgoing: null = доставлено (✓),
  // ISO-timestamp = юзер прочитал (✓✓). Только для direction='outgoing'.
  read_by_user_at: string | null;
  created_at: string;
}

const DIALOGS_REFRESH = 10_000;
const THREAD_REFRESH = 5_000;


// ─── helpers ────────────────────────────────────────────────────────────────

const nameOf = (d: { first_name?: string | null; tg_username?: string | null; tg_id?: number }) =>
  d.first_name || (d.tg_username ? `@${d.tg_username}` : `tg:${d.tg_id ?? "?"}`);

const previewOf = (blocks: DialogItem["last_blocks"]): string => {
  if (!blocks || blocks.length === 0) return "(пусто)";
  const b = blocks[0];
  switch (b.type) {
    case "text": return b.text?.slice(0, 80) || "";
    case "html":
      // строкируем HTML для preview
      return (b.html || "").replace(/<[^>]+>/g, "").slice(0, 80);
    case "photo":     return "📷 Фото" + (b.caption ? `: ${b.caption.slice(0, 40)}` : "");
    case "video":     return "🎥 Видео" + (b.caption ? `: ${b.caption.slice(0, 40)}` : "");
    case "audio":     return "🎵 Аудио";
    case "voice":     return "🎤 Голосовое";
    case "video_note":return "📹 Кружок";
    case "document":  return "📄 " + (b.file_name || "Файл");
    case "sticker":   return "💠 Стикер";
    case "unsupported": return "(неподдерживаемый тип)";
    default:          return `(${b.type})`;
  }
};


// «Был N мин назад / N часов назад / вчера» — для оффлайн-юзеров.
const fmtRelativeSeen = (iso: string | null): string => {
  if (!iso) return "не заходил";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "только что";
  const sec = Math.floor(ms / 1000);
  if (sec < 60)         return `${sec} сек назад`;
  const min = Math.floor(sec / 60);
  if (min < 60)         return `${min} мин назад`;
  const hr = Math.floor(min / 60);
  if (hr < 24)          return `${hr} ч назад`;
  const days = Math.floor(hr / 24);
  if (days < 7)         return `${days} д назад`;
  return new Date(iso).toLocaleDateString("ru-RU");
};


// URL-проксипроксипрокс TG-файла через защищённый admin endpoint.
const mediaSrc = (fileId: unknown): string =>
  `${API_URL}/admin/chat/media/${encodeURIComponent(String(fileId))}`;


// Размер файла → «1.2 MB / 480 KB»
const fmtSize = (bytes: unknown): string | null => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 1024)            return `${n} B`;
  if (n < 1024 * 1024)     return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3)       return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
};


const renderBlock = (b: ChatMessage["content_blocks"][0], i: number, isOutgoing: boolean) => {
  const t = b.type as string;
  const fileId = b.file_id as string | undefined;
  const caption = b.caption as string | undefined;

  const captionEl = caption
    ? <div style={{ marginTop: 6, fontSize: 13 }}>{caption}</div>
    : null;

  if (t === "text") return <div key={i} style={{ whiteSpace: "pre-wrap" }}>{(b.text as string) || ""}</div>;

  if (t === "html") return (
    <div key={i} className="tg-html"
         dangerouslySetInnerHTML={{ __html: (b.html as string) || "" }} />
  );

  if (t === "photo" && fileId) {
    const src = mediaSrc(fileId);
    return (
      <div key={i}>
        <img src={src} alt="photo" loading="lazy"
             style={{ maxWidth: "100%", maxHeight: 320, borderRadius: 8,
                      display: "block", cursor: "zoom-in" }}
             onClick={() => window.open(src, "_blank", "noopener,noreferrer")} />
        {captionEl}
      </div>
    );
  }

  if (t === "video" && fileId) {
    return (
      <div key={i}>
        <video src={mediaSrc(fileId)} controls preload="metadata"
               style={{ maxWidth: "100%", maxHeight: 320, borderRadius: 8,
                        display: "block", background: "#000" }} />
        {captionEl}
      </div>
    );
  }

  if (t === "video_note" && fileId) {
    return (
      <div key={i}>
        <video src={mediaSrc(fileId)} controls preload="metadata"
               style={{ width: 220, height: 220, borderRadius: "50%",
                        objectFit: "cover", background: "#000" }} />
      </div>
    );
  }

  if ((t === "voice" || t === "audio") && fileId) {
    const dur = b.duration as number | undefined;
    const title = b.title as string | undefined;
    return (
      <div key={i} style={{ minWidth: 240 }}>
        {title ? <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div> : null}
        <audio src={mediaSrc(fileId)} controls preload="metadata"
               style={{ width: "100%", marginTop: title ? 4 : 0 }} />
        <div style={{ fontSize: 10, opacity: 0.65, marginTop: 2 }}>
          {t === "voice" ? "🎤 голосовое" : "🎵 аудио"}
          {dur ? ` · ${dur}s` : ""}
        </div>
        {captionEl}
      </div>
    );
  }

  if (t === "document" && fileId) {
    const name = (b.file_name as string) || "Файл";
    const size = fmtSize(b.file_size);
    return (
      <div key={i}>
        <a href={mediaSrc(fileId)} download={name}
           target="_blank" rel="noopener noreferrer"
           style={{
             display: "inline-flex", alignItems: "center", gap: 10,
             padding: "8px 12px",
             background: isOutgoing ? "rgba(255,255,255,0.15)" : "#f5f5f5",
             border: isOutgoing ? "1px solid rgba(255,255,255,0.25)" : "1px solid #e8e8e8",
             borderRadius: 8,
             color: "inherit", textDecoration: "none",
             maxWidth: "100%",
           }}
           title="Скачать">
          <span style={{ fontSize: 22, lineHeight: 1 }}>📄</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{name}</div>
            {size ? <div style={{ fontSize: 11, opacity: 0.7 }}>{size}</div> : null}
          </span>
        </a>
        {captionEl}
      </div>
    );
  }

  if (t === "sticker" && fileId) {
    // Sticker может быть WEBP (статика) или TGS/Lottie (анимация).
    // <img> покажет WEBP, для TGS — fallback на emoji.
    const emoji = b.emoji as string | undefined;
    return (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <img src={mediaSrc(fileId)} alt={emoji || "sticker"}
             style={{ maxWidth: 128, maxHeight: 128 }}
             onError={(e) => { e.currentTarget.style.display = "none"; }} />
        {emoji ? <span style={{ fontSize: 32 }}>{emoji}</span> : null}
      </div>
    );
  }

  if (t === "unsupported") {
    return <div key={i} style={{ fontSize: 12, opacity: 0.7 }}>
      ⚠ {String(b.note || "тип сообщения не поддерживается")}
    </div>;
  }

  // Безопасный fallback (например fileId не пришёл).
  return <div key={i} style={{ fontSize: 12, color: "#999" }}>(блок «{t}» без данных)</div>;
};


// ─── component ──────────────────────────────────────────────────────────────

export const DialogsPage = () => {
  const [dialogs, setDialogs] = useState<DialogItem[]>([]);
  const [loadingDialogs, setLoadingDialogs] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);

  // Auto-select user from URL ?user=<tg_id>. Triggered when админ кликает
  // кнопку «💬 Написать» в Users list — переход сразу открывает диалог.
  // Работает даже если юзер ещё не в списке диалогов (новый чат).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const userParam = searchParams.get("user");
    if (userParam) {
      const uid = parseInt(userParam, 10);
      if (!isNaN(uid)) {
        setSelected(uid);
        // Убираем query param чтобы при обновлении страницы не было
        // повторного триггера + URL остаётся чистым.
        const next = new URLSearchParams(searchParams);
        next.delete("user");
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [user, setUser] = useState<DialogItem | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const threadEnd = useRef<HTMLDivElement | null>(null);

  const loadDialogs = async (silent = false) => {
    if (!silent) setLoadingDialogs(true);
    try {
      const r = await fetch(`${API_URL}/admin/chat/dialogs?per_page=100`,
                            { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setDialogs(d.items || []);
      }
    } finally {
      if (!silent) setLoadingDialogs(false);
    }
  };

  const loadThread = async (uid: number, silent = false) => {
    if (!silent) setLoadingThread(true);
    try {
      const r = await fetch(`${API_URL}/admin/chat/dialogs/${uid}/messages`,
                            { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setMessages(d.messages || []);
        setUser({
          ...d.user, last_blocks: [], last_message_at: null,
          unread: 0, last_direction: "incoming",
        });
      }
    } finally {
      if (!silent) setLoadingThread(false);
    }
  };

  const markRead = async (uid: number) => {
    await fetch(`${API_URL}/admin/chat/dialogs/${uid}/mark-read`,
                { method: "POST", credentials: "include" });
    loadDialogs(true);
  };

  const onSelect = (uid: number) => {
    setSelected(uid);
    loadThread(uid);
    markRead(uid);
  };

  useEffect(() => { loadDialogs(); }, []);
  // poll dialogs list
  useEffect(() => {
    const t = setInterval(() => loadDialogs(true), DIALOGS_REFRESH);
    return () => clearInterval(t);
  }, []);
  // poll active thread
  useEffect(() => {
    if (selected === null) return;
    const t = setInterval(() => loadThread(selected, true), THREAD_REFRESH);
    return () => clearInterval(t);
  }, [selected]);
  // scroll-to-bottom on new messages
  useEffect(() => {
    if (threadEnd.current) {
      threadEnd.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length]);

  return (
    <div style={{ display: "flex", height: "calc(100vh - 96px)",
                  background: "#fff", borderRadius: 6, overflow: "hidden",
                  border: "1px solid #e8e8e8" }}>
      {/* LEFT: list */}
      <div style={{ width: 340, borderRight: "1px solid #e8e8e8",
                    display: "flex", flexDirection: "column" }}>
        <div style={{ padding: 12, borderBottom: "1px solid #e8e8e8",
                      display: "flex", justifyContent: "space-between" }}>
          <Typography.Text strong>Диалоги ({dialogs.length})</Typography.Text>
          <Button size="small" type="text" icon={<ReloadOutlined />}
                  onClick={() => loadDialogs()} title="Обновить" />
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loadingDialogs ? (
            <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>
          ) : dialogs.length === 0 ? (
            <Empty description="Пока нет диалогов" style={{ marginTop: 60 }} />
          ) : (
            <List
              dataSource={dialogs}
              rowKey="tg_id"
              renderItem={(d) => {
                const active = selected === d.tg_id;
                return (
                  <List.Item
                    onClick={() => onSelect(d.tg_id)}
                    style={{
                      padding: "10px 14px", cursor: "pointer",
                      background: active ? "#e6f4ff" : undefined,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <Space style={{ width: "100%", justifyContent: "space-between" }}>
                        <Space size={8}>
                          <Badge
                            dot
                            color={d.online ? "#52c41a" : "#bfbfbf"}
                            offset={[-4, 28]}
                            title={d.online ? "🟢 онлайн" : `был ${fmtRelativeSeen(d.last_seen_at)}`}
                          >
                            <Avatar size={32} icon={<UserOutlined />} />
                          </Badge>
                          <div>
                            <div>
                              <Typography.Text strong style={{ fontSize: 13 }}>
                                {nameOf(d)}
                              </Typography.Text>
                              {d.bot_blocked && (
                                <Tag color="red" style={{ fontSize: 10, marginLeft: 6 }}>blocked</Tag>
                              )}
                            </div>
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                              {d.tg_username ? `@${d.tg_username}` : `tg:${d.tg_id}`}
                            </Typography.Text>
                          </div>
                        </Space>
                        <Space direction="vertical" size={0} style={{ textAlign: "right" }}>
                          <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                            {d.last_message_at
                              ? new Date(d.last_message_at).toLocaleTimeString("ru-RU",
                                  { hour: "2-digit", minute: "2-digit" })
                              : "—"}
                          </Typography.Text>
                          {d.unread > 0 && (
                            <Badge count={d.unread} style={{ backgroundColor: "#ff4d4f" }} />
                          )}
                        </Space>
                      </Space>
                      <Typography.Paragraph
                        type={d.last_direction === "outgoing" ? "secondary" : undefined}
                        ellipsis style={{ marginTop: 4, marginBottom: 0, fontSize: 12 }}
                      >
                        {d.last_direction === "outgoing" ? "→ " : ""}
                        {previewOf(d.last_blocks)}
                      </Typography.Paragraph>
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
        </div>
      </div>

      {/* RIGHT: thread + composer */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {selected === null ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center",
                        justifyContent: "center" }}>
            <Empty image={<MessageOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />}
                   description="Выберите диалог слева" />
          </div>
        ) : (
          <>
            {/* header */}
            <div style={{ padding: "10px 16px", borderBottom: "1px solid #e8e8e8",
                          display: "flex", justifyContent: "space-between" }}>
              <Space>
                <Badge
                  dot
                  color={user?.online ? "#52c41a" : "#bfbfbf"}
                  offset={[-4, 32]}
                >
                  <Avatar size={36} icon={<UserOutlined />} />
                </Badge>
                <div>
                  <Space size={6} align="center">
                    <Typography.Text strong>{user ? nameOf(user) : "—"}</Typography.Text>
                    {user?.bot_blocked && (
                      <Tag color="red" style={{ fontSize: 10, margin: 0 }}
                           title="Юзер заблокировал бота — отправка недоступна">
                        🚫 Заблокировал бота
                      </Tag>
                    )}
                    {user?.flagged_abuse && (
                      <Tag color="volcano" style={{ fontSize: 10, margin: 0 }}>
                        ⚠ flagged
                      </Tag>
                    )}
                  </Space>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {user?.online ? (
                      <span style={{ color: "#52c41a", fontWeight: 500 }}>🟢 онлайн</span>
                    ) : user?.last_seen_at ? (
                      <span>был {fmtRelativeSeen(user.last_seen_at)}</span>
                    ) : (
                      <span>не заходил в бот</span>
                    )}
                    {user?.tg_username ? ` · @${user.tg_username}` : ""}
                    {" · "}tg:{selected}
                  </Typography.Text>
                </div>
              </Space>
              <Button size="small" type="text" icon={<ReloadOutlined />}
                      onClick={() => loadThread(selected)} title="Обновить" />
            </div>

            {/* messages — фиксированно занимает мин. 240px, composer растёт */}
            <div style={{ flex: "1 1 50%", overflowY: "auto", padding: 16,
                          background: "#fafafa", minHeight: 240 }}>
              {loadingThread ? (
                <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
              ) : messages.length === 0 ? (
                <Empty description="Сообщений пока нет" style={{ marginTop: 60 }} />
              ) : (
                messages.map((m) => {
                  const isOutgoing = m.direction === "outgoing";
                  const isRead = isOutgoing && !!m.read_by_user_at;
                  // Подсветка прочитанного: чуть светлее голубой ↘ ярче-голубой,
                  // чтобы взгляд админа сразу видел «юзер видел». Не прочитано —
                  // насыщеннее (наше «свежее» состояние).
                  const bg = isOutgoing
                    ? (isRead ? "#4ea3ff" : "#1677ff")
                    : "#fff";
                  return (
                    <div key={m.id} style={{
                      display: "flex",
                      justifyContent: isOutgoing ? "flex-end" : "flex-start",
                      marginBottom: 10,
                    }}>
                      <div style={{
                        maxWidth: "70%",
                        padding: "8px 12px", borderRadius: 12,
                        background: bg,
                        color: isOutgoing ? "#fff" : undefined,
                        border: isOutgoing ? undefined : "1px solid #e8e8e8",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        opacity: isRead ? 0.92 : 1,
                      }}>
                        {(m.content_blocks || []).map((b, i) => renderBlock(b, i, isOutgoing))}
                        <div style={{
                          marginTop: 4,
                          fontSize: 10,
                          opacity: 0.85,
                          textAlign: "right",
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 4,
                        }}>
                          <span>
                            {new Date(m.created_at).toLocaleTimeString("ru-RU",
                              { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {isOutgoing && (
                            <span title={isRead
                              ? `Прочитано: ${m.read_by_user_at ? new Date(m.read_by_user_at).toLocaleString("ru-RU") : ""}`
                              : "Отправлено, юзер ещё не открыл бот"}
                              style={{
                                fontSize: 12,
                                lineHeight: 1,
                                color: isRead ? "#a5e3ff" : "#cfe2ff",
                                fontFamily: "monospace",
                                marginLeft: 2,
                              }}>
                              {isRead ? "✓✓" : "✓"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={threadEnd} />
            </div>

            {/* composer (если юзер заблокировал бота — composer показывается
                но disabled с Alert; история выше остаётся read-only) */}
            <ChatComposer
              userId={selected}
              botBlocked={!!user?.bot_blocked}
              onSent={() => {
                loadThread(selected);
                loadDialogs(true);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};
